import React, { useMemo, useCallback, useState } from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { Constants, isLotteryDrawOpen } from "@eagerdog/constants";
import { helperService } from "../../services/helper.service";

import { IEventSummary, IAddress, IDog } from "@eagerdog/interfaces";
import Event from "../../routes/Event/Event";

import HorizontalScroll from "../HorizontalScroll/HorizontalScroll";
import DogPortrait from "../DogPortrait/DogPortrait";
import { Modal, useModal } from "../Modal/Modal";

import "./EventCard.scss";
import Button from "../Button/Button";
import PublishEventModal from "../PublishEventModal/PublishEventModal";

interface IEventCardPrice {
  amount: number,
  isEarlyBird: boolean,
  earlyBirdEnd?: Date
}

interface IProps {
  event: IEventSummary,
  action: { text: string; link: string, disabled?: boolean },
  isAdmin?: boolean,
  isPastEvent?: boolean,
  afterUpdate?: () => void
}

const EventCard: React.FC<IProps> = (props) => {
  const { isShown, toggle } = useModal();
  const [modalContent, setModalContent] = useState<string>();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: props.event.currency?.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const eventModalContent = (
    <React.Fragment>
      <div className="scrollWrap">
        <Event eid={props.event.handle} />
      </div>
    </React.Fragment>
  );

  const getModalContent = () => {
    switch (modalContent) {
      case "eventModal":
        return eventModalContent;
      case "publishEvent":
        return <PublishEventModal event={props.event} afterUpdate={props.afterUpdate} toggle={() => { toggle(); window.history.pushState("", "", "/events"); }}/>
      default:
        return <></>;
    }
  }

  const formatAddress = (address: IAddress) => {
    let result: string = "";

    if (address.line1) {
      result += address.line1 + ", ";
    }

    if (address.line2) {
      result += address.line2 + ", ";
    }

    if (address.city) {
      result += address.city + ", ";
    }

    if (address.state) {
      result += address.state + ", ";
    }

    if (address.country) {
      result += address.country;
    }

    return result;
  };

  const getShowTypes = () => {
    let _showTypes: string[] = [];

    for (let s in props.event.show_types) {
      if (!_showTypes.includes(props.event.show_types[s].show_type))
        _showTypes.push(props.event.show_types[s].show_type);
    }
  
    return _showTypes;
  };

  const getMin = useCallback(
    (event: IEventSummary) => {
      let min:IEventCardPrice = { amount: 99999, isEarlyBird: false };

      for (let s in props.event.show_types) {
        const showType = props.event.show_types[s];
        const currentDate = new Date();
        
        // Check if early bird pricing is active
        const isEarlyBirdActive = showType.early_bird_fee != null && 
          showType.early_bird_end_date && 
          currentDate <= new Date(showType.early_bird_end_date);
        
        // Determine the current effective price
        const effectivePrice = isEarlyBirdActive ? showType.early_bird_fee : showType.fee;
        
                 // Update minimum if this price is lower
         if (effectivePrice != null && effectivePrice < min.amount) {
           min = {
             amount: effectivePrice,
             isEarlyBird: !!isEarlyBirdActive,
             earlyBirdEnd: isEarlyBirdActive ? showType.early_bird_end_date : undefined
           };
         }
      }

      // If no valid prices found, reset to -1 to indicate no pricing
      if (min.amount === 99999) {
        min.amount = -1;
      }
      
      return min;
    },
    [props.event.show_types]
  );

  const getMax = useCallback(
    (event: IEventSummary) => {
      let max:IEventCardPrice = { amount: -1, isEarlyBird: false };

      for (let s in props.event.show_types) {
        const showType = props.event.show_types[s];
        const currentDate = new Date();
        
        // Check if early bird pricing is active
        const isEarlyBirdActive = showType.early_bird_fee != null && 
          showType.early_bird_end_date && 
          currentDate <= new Date(showType.early_bird_end_date);
        
        // Determine the current effective max price
        // Use max_fee if available (for shows with varying element fees), otherwise fall back to fee
        const effectiveMaxPrice = isEarlyBirdActive 
          ? (showType.max_early_bird_fee ?? showType.early_bird_fee)
          : (showType.max_fee ?? showType.fee);
        
        // Update maximum if this price is higher
        if (effectiveMaxPrice != null && effectiveMaxPrice > max.amount) {
          max = {
            amount: effectiveMaxPrice,
            isEarlyBird: !!isEarlyBirdActive,
            earlyBirdEnd: isEarlyBirdActive ? showType.early_bird_end_date : undefined
          };
        }
      }

      return max;
    },
    [props.event.show_types]
  );

  const min:IEventCardPrice = useMemo(() => getMin(props.event), [getMin, props.event]);
  const max:IEventCardPrice = useMemo(() => getMax(props.event), [getMax, props.event]);

  return (
    <div className="EventCard">
      <div className="name">
        <span
          onClick={(e) => {
            window.history.pushState("", "", "/e/" + props.event.handle);
            setModalContent("eventModal");
            toggle();
          }}
        >
          {props.event.title}
        </span>
      </div>
      <div className="club">
        {(props.event.club_id || props.event.club?._id) ? (
          <Link className="clubLink" to={`/k/${props.event.club_id || props.event.club?._id}`}>
            {props.event.club_name ?? props.event.club?.club_name}
          </Link>
        ) : (
          props.event.club_name ?? props.event.club?.club_name
        )}
      </div>
      <div className="labelsWrapper">
        <span className={"eventStatus " + (props.isPastEvent ? "finished" : props.event.status.toLowerCase())}>
          {props.isPastEvent ? "Finished" : helperService.getEventStatus(props.event.status)}
        </span>
        {isLotteryDrawOpen(props.event) && !props.isPastEvent && <span className="lotteryLabel">Random Draw</span>}
      </div>

      <div className="eventType">{props.event.sanctioning_club}</div>
      <div className="date">
        <span>
          {moment(props.event.start_date).format("MMM D, YYYY")} - {moment(props.event.end_date).format("MMM D, YYYY")}
        </span>
      </div>
      <div className="location">{formatAddress(props.event.address)}</div>
      {getShowTypes().length > 0 && <HorizontalScroll>
        {getShowTypes().map((show: string, index: number) => {
          return (
            <li key={index} className="show">
              {show}
            </li>
          );
        })}
      </HorizontalScroll>}
      {getShowTypes().length === 0 && <HorizontalScroll>
        <li className="show">No Show Types</li>
      </HorizontalScroll>}
      {!props.event?.is_registered && props.event?.status === Constants.event_status.published && <div className="price">
        <div className="range">
          {(props.event.currency === "usd" || props.event.currency === "cad" || props.event.currency === undefined) && <>
            {(min.amount !== max.amount && max.amount > 0) && (
              <><div className="minPrice"><span>$</span>{min.amount}</div>-<div className="maxPrice"><span>$</span>{max.amount}</div></>
            )}
            {min.amount === max.amount && min.amount !== -1 && (
              <div className="minPrice"><span>$</span>{min.amount}</div>
            )}
            {((min.amount !== max.amount && min.amount < 0) || (min.amount === max.amount && min.amount === -1)) && (
              <><div className="minPrice">No Price Set</div></>
            )}
          </>}
          {(props.event.currency !== "usd" && props.event.currency !== "cad" && props.event.currency !== undefined) && <>
            {(min.amount !== max.amount && max.amount > 0) && (
              <><div className="minPrice">{formatter.format(min.amount)}</div>-<div className="maxPrice">{formatter.format(max.amount)}</div></>
            )}
            {min.amount === max.amount && (
              <div className="minPrice">{formatter.format(min.amount)}</div>
            )}
            {(min.amount !== max.amount && max.amount < 0) && (
              <><div className="minPrice">No Price Set</div></>
            )}
          </>}
          {min.isEarlyBird && <span className="early-bird">Early Bird</span>}
        </div>
        {<div className="disclaimer">prices in {props.event.currency?.toUpperCase() || "USD"}{min.earlyBirdEnd && moment(new Date()).format("X") < moment(min.earlyBirdEnd).format("X") && ", pre-entry end date " + moment(min.earlyBirdEnd).format("MM/DD/YYYY")}</div>}
      </div>}
      {!props.event?.is_registered && props.event?.status === Constants.event_status.upcoming && <div className="price">
        <div className="openDateLabel">Event Opens On</div>
        <div className="openDate">{moment(props.event.publish_at).tz(props.event.timezone || moment.tz.guess()).format("MMM Do, YYYY @ ha zz")}</div>
      </div>}
      {!props.event?.is_registered && props.event?.status === Constants.event_status.draft && <div className="price">
        <div className="openDateLabel">This event is not visible to participants.</div>
      </div>}
      {props.event?.is_registered && <div className="price registeredDogs">
        <ul>
          {props?.event?.registered_dogs?.map((dog: IDog, index:number) => {
            return(<li key={index}><DogPortrait className={"smallDogPortrait"} showName={false} dog={dog} /></li>);
          })}
          <li className="tag">Registered</li>
        </ul>
      </div>}
      <div className="action">
        {props.event.status === Constants.event_status.draft && props.isAdmin && (
          <Button
            className="premiumLink"
            onClick={() => {
              setModalContent("publishEvent");
              toggle();
              window.history.pushState("", "", "/e/" + props.event.handle);
            }}
          >
            Publish Now
          </Button>
        )}
        {!props.action.disabled && (
          <Link className={(props.event.premium_file_url && !props.isPastEvent || props.event.status === Constants.event_status.draft && props.isAdmin) ? "" : "solo"} to={props.action.link}>{props.action.text}</Link>
        )}
        {props.action.disabled && (
          <div className="disabled-register"><span>Event Full</span></div>
        )}
        {!!props.event.premium_file_url && !props.isPastEvent && <a href={props.event.premium_file_url} className="premiumLink" target="_blank" rel="noreferrer">View Premium</a>}
      </div>
      <Modal
        className={modalContent}
        isShown={isShown}
        hide={() => {
          toggle()
          window.history.pushState("", "", "/events");
        }}
        expand={true}
        modalContent={getModalContent()}
      />
    </div>
  );
};

export default EventCard;
